import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { SpinnerGap, Warning, Key, Archive, CreditCard, Lightning, ArrowRight, Buildings, User } from "@phosphor-icons/react";
import { UsageMeter } from "./shared";

export function BillingTab() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storageAddons, setStorageAddons] = useState(null);
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [purchasingAddon, setPurchasingAddon] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const resp = await api.get("/usage");
        setUsage(resp.data);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, [api]);

  useEffect(() => {
    const fetchAddons = async () => {
      try {
        const resp = await api.get("/storage/addons");
        setStorageAddons(resp.data);
      } catch { /* silent */ }
    };
    fetchAddons();
  }, [api]);

  const handleBuyStorage = async (addonId) => {
    setPurchasingAddon(addonId);
    try {
      const resp = await api.post("/stripe/create-addon-checkout", {
        addon_id: addonId,
        origin_url: window.location.origin,
      });
      if (resp.data?.checkout_url) {
        window.location.href = resp.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start checkout");
    } finally {
      setPurchasingAddon(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><SpinnerGap size={28} className="animate-spin text-[#FF4400]" /></div>;
  }

  if (!usage) {
    return <p className="text-sm text-[var(--text-muted)] text-center py-8">Unable to load billing info.</p>;
  }

  const tier = usage.tier || "free_beta";
  const tierLabel = usage.tier_label || "Free Beta";
  const genUsed = usage.generations_used || 0;
  const genLimit = usage.generations_limit;
  const fairUse = usage.fair_use_limit;
  const creditBalance = usage.credit_balance || 0;
  const wsUsed = usage.businesses_used || 0;
  const wsLimit = usage.businesses_limit;
  const seatsUsed = usage.seats_used || 0;
  const seatsIncluded = usage.seats_included || 1;
  const rbUsed = usage.resource_bank_used || 0;
  const rbLimit = usage.resource_bank_limit;
  const rbBaseLimit = usage.resource_bank_base_limit || rbLimit;
  const rbAddonSlots = usage.resource_bank_addon_slots || 0;
  const chatUsed = usage.chat_messages_used || 0;
  const chatLimit = usage.chat_messages_limit;
  const chatSoftWarning = !!usage.chat_soft_warning;
  const chatHardStop = !!usage.chat_hard_stop;

  const isFreeTier = tier === "free_beta";
  const rbPct = rbLimit && rbLimit !== -1 ? (rbUsed / rbLimit) * 100 : 0;
  const rbWarning = rbPct >= 80 && rbPct < 100;
  const rbFull = rbLimit && rbLimit !== -1 && rbUsed >= rbLimit;

  return (
    <div className="space-y-6">
      {/* Plan Card */}
      <section className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6" data-testid="billing-plan-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1">Current Plan</h3>
            <div className="flex items-center gap-2">
              <span className="font-['Cormorant_Garamond'] text-2xl font-bold text-[var(--text-primary)]">{tierLabel}</span>
              <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Active</span>
            </div>
          </div>
          {isFreeTier && (
            <button
              onClick={() => navigate("/pricing")}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FF4400] hover:bg-[#E63E00] text-white text-sm font-semibold transition-all"
              data-testid="billing-upgrade-cta"
            >
              View Plans <ArrowRight size={14} />
            </button>
          )}
        </div>
      </section>

      {/* Usage Meters */}
      <section className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6 space-y-5" data-testid="billing-usage-section">
        <h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Usage This Month</h3>

        <UsageMeter label="AI Generations" used={genUsed} limit={genLimit || fairUse} suffix={fairUse && !genLimit ? " (fair use)" : ""} icon={Lightning} testId="billing-gen-meter" />
        <UsageMeter label="Brainstorm Chat Messages" used={chatUsed} limit={chatLimit} icon={Lightning} testId="billing-chat-meter" />

        {chatSoftWarning && !chatHardStop && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="chat-warning-banner">
            <Warning size={16} className="text-amber-400 shrink-0" />
            <p className="text-xs text-amber-400">
              You&apos;re close to your brainstorm chat limit ({chatUsed}/{chatLimit} this month).
            </p>
            <button onClick={() => navigate("/pricing")} className="ml-auto text-xs font-semibold text-[#FF4400] hover:underline whitespace-nowrap" data-testid="chat-warning-upgrade-btn">View Plans</button>
          </div>
        )}

        {chatHardStop && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="chat-hard-stop-banner">
            <Warning size={16} className="text-red-400 shrink-0" weight="fill" />
            <p className="text-xs text-red-400">Brainstorm chat limit reached for this month. Upgrade or buy a pack to continue.</p>
            <button onClick={() => navigate("/pricing")} className="ml-auto text-xs font-semibold text-[#FF4400] hover:underline whitespace-nowrap" data-testid="chat-hard-stop-upgrade-btn">Upgrade</button>
          </div>
        )}

        {(creditBalance > 0 || (genLimit && genUsed >= genLimit)) && (
          <div className="space-y-1.5" data-testid="credit-balance-display">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <CreditCard size={16} className="text-amber-400" />
                <span className="text-sm text-[var(--text-secondary)]">Purchased Credits</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[var(--text-primary)]">{creditBalance}</span>
                <button
                  onClick={() => navigate("/pricing")}
                  className="text-[10px] px-2 py-1 rounded-md bg-[var(--bg-base)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[#FF4400] hover:border-[#FF4400]/30 transition-colors"
                  data-testid="buy-credits-settings-btn"
                >
                  Buy More
                </button>
              </div>
            </div>
            <p className="text-[10px] text-[var(--text-muted)] px-1">
              Monthly allowance used first. Credits expire 6 months after purchase. <a href="/legal/credits" className="text-[#FF4400] hover:underline">Credit policy</a>
            </p>
          </div>
        )}

        <UsageMeter label="Business Workspaces" used={wsUsed} limit={wsLimit} icon={Buildings} testId="billing-ws-meter" />
        <UsageMeter label="Team Seats" used={seatsUsed} limit={seatsIncluded === -1 ? null : seatsIncluded} icon={User} testId="billing-seats-meter" />
        <UsageMeter label="Resource Bank Files" used={rbUsed} limit={rbLimit === -1 ? null : rbLimit} suffix={rbAddonSlots > 0 ? ` (${rbBaseLimit} base + ${rbAddonSlots} add-on)` : " per workspace"} icon={Archive} testId="billing-rb-meter" />

        {rbWarning && !rbFull && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="rb-warning-banner">
            <Warning size={16} className="text-amber-400 shrink-0" />
            <p className="text-xs text-amber-400">
              You're using {Math.round(rbPct)}% of your resource bank. Consider adding more storage.
            </p>
            <button onClick={() => setShowStorageModal(true)} className="ml-auto text-xs font-semibold text-[#FF4400] hover:underline whitespace-nowrap" data-testid="rb-warning-buy-btn">Buy More</button>
          </div>
        )}

        {rbFull && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="rb-full-banner">
            <Warning size={16} className="text-red-400 shrink-0" weight="fill" />
            <p className="text-xs text-red-400">Resource bank full. Buy more storage to continue adding files.</p>
            <button onClick={() => setShowStorageModal(true)} className="ml-auto text-xs font-semibold text-[#FF4400] hover:underline whitespace-nowrap" data-testid="rb-full-buy-btn">Buy Storage</button>
          </div>
        )}
      </section>

      {/* Storage Add-on Section */}
      {storageAddons && (
        <section className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6 space-y-4" data-testid="storage-addon-section">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1">Storage Add-ons</h3>
              <p className="text-xs text-[var(--text-muted)]">
                {storageAddons.active_addons > 0
                  ? `${storageAddons.active_addons} active add-on${storageAddons.active_addons > 1 ? "s" : ""} (+${storageAddons.current_addon_slots} files)`
                  : "Expand your resource bank with additional file storage"
                }
              </p>
            </div>
            {storageAddons.active_addons < storageAddons.max_addons && (
              <button
                onClick={() => setShowStorageModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF4400]/10 hover:bg-[#FF4400]/20 text-[#FF4400] text-xs font-semibold transition-all border border-[#FF4400]/20"
                data-testid="buy-storage-cta"
              >
                <Archive size={14} /> Add Storage
              </button>
            )}
          </div>
          {storageAddons.active_addons >= storageAddons.max_addons && (
            <p className="text-xs text-[var(--text-muted)]">
              Maximum {storageAddons.max_addons} add-ons reached. Contact us for Enterprise needs.
            </p>
          )}
        </section>
      )}

      {/* Storage Add-on Purchase Modal */}
      <Dialog open={showStorageModal} onOpenChange={setShowStorageModal}>
        <DialogContent className="sm:max-w-md bg-[var(--bg-card)] border-[var(--border)]">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-xl text-[var(--text-primary)]">
              Add Resource Bank Storage
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {storageAddons?.packs?.map((pack) => (
              <button
                key={pack.id}
                onClick={() => handleBuyStorage(pack.id)}
                disabled={purchasingAddon === pack.id || (storageAddons.active_addons >= storageAddons.max_addons)}
                className="w-full flex items-center justify-between p-4 rounded-lg border border-[var(--border)] hover:border-[#FF4400]/40 hover:bg-[#FF4400]/5 transition-all group disabled:opacity-50"
                data-testid={`storage-pack-${pack.id}`}
              >
                <div className="text-left">
                  <span className="text-sm font-semibold text-[var(--text-primary)] group-hover:text-[#FF4400] transition-colors">
                    {pack.label}
                  </span>
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">Recurring monthly subscription</p>
                </div>
                <div className="text-right">
                  {purchasingAddon === pack.id ? (
                    <SpinnerGap size={18} className="animate-spin text-[#FF4400]" />
                  ) : (
                    <span className="text-sm font-['JetBrains_Mono'] font-semibold text-[var(--text-primary)]">
                      ${(pack.price_cents / 100).toFixed(0)}/mo
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
          <div className="space-y-2 pt-1 border-t border-[var(--border)]">
            <p className="text-[10px] text-[var(--text-muted)] leading-relaxed pt-2">
              Storage add-ons are monthly subscriptions. Slots are active while the subscription is active. If canceled, add-on slots are removed at the end of the billing cycle. Max {storageAddons?.max_addons || 3} add-ons per account. Existing files remain accessible but no new files can be added above the base limit.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* BYOK Notice */}
      {usage.byok_required && (
        <section className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5" data-testid="billing-byok-notice">
          <div className="flex items-start gap-3">
            <Key size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-amber-400 mb-1">BYOK Required</h4>
              <p className="text-xs text-[var(--text-muted)] leading-relaxed">
                Your plan requires your own OpenRouter API key. Add or update it in the API Keys tab.
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
